import { Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import { Skeleton } from '../../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { PlansTableProps } from '../types';
import { discountPercent, durationDays, durationLabel, formatMoney, groupDigits, perDay } from '../planFormat';

const num = 'text-right tabular-nums';
const isUzs = (currency: string) => !currency || currency.toUpperCase() === 'UZS';

/** Таблица тарифов одного прайс-листа (базового или ценового варианта) — макет «Тарифы». */
export function PlansTable({ plans, onEdit, onDelete, onToggleActive, togglingId, selectedId }: PlansTableProps) {
  return (
    <Table className="min-w-[860px]">
      <TableHeader>
        <TableRow>
          <TableHead>Код</TableHead>
          <TableHead>Название</TableHead>
          <TableHead>Длительность</TableHead>
          <TableHead className="text-right">Цена, сум</TableHead>
          <TableHead className="text-right">Старая цена</TableHead>
          <TableHead className="text-right">В день</TableHead>
          <TableHead>Активен</TableHead>
          <TableHead className="text-right">
            <span className="sr-only">Действия</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {plans.map((plan) => {
          const days = durationDays(plan.duration_value, plan.duration_unit);
          const daily = perDay(plan.price, days);
          const off = discountPercent(plan.price, plan.old_price);
          return (
            <TableRow key={plan.id} selected={selectedId === plan.id}>
              <TableCell>
                <code className="rounded-lg bg-muted px-2 py-[3px] font-mono text-xs">{plan.code}</code>
              </TableCell>
              <TableCell className="font-semibold">{plan.title}</TableCell>
              <TableCell className="whitespace-nowrap">{durationLabel(plan.duration_value, plan.duration_unit)}</TableCell>
              <TableCell className={`${num} whitespace-nowrap font-display font-bold`}>
                {isUzs(plan.currency) ? groupDigits(plan.price) : formatMoney(plan.price, plan.currency)}
              </TableCell>
              <TableCell className={`${num} whitespace-nowrap text-text-2`}>
                {plan.old_price ? (
                  <>
                    <s>{groupDigits(plan.old_price)}</s>
                    {off !== null && <span className="ml-1.5 text-xs font-bold text-pos">−{off}%</span>}
                  </>
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell className={`${num} whitespace-nowrap`}>
                {daily !== null ? formatMoney(daily, plan.currency) : '—'}
              </TableCell>
              <TableCell>
                <span className="flex items-center gap-2">
                  <Switch
                    checked={plan.is_active}
                    disabled={togglingId === plan.id}
                    onCheckedChange={(checked) => onToggleActive(plan, checked)}
                    aria-label={`${plan.title}: ${plan.is_active ? 'активен' : 'неактивен'}`}
                  />
                  <span className="text-[13px] text-text-2">{plan.is_active ? 'Показан' : 'Скрыт'}</span>
                </span>
              </TableCell>
              <TableCell className="text-right">
                <div className="inline-flex items-center gap-2">
                  <Button variant="secondary" size="icon-sm" aria-label={`Изменить ${plan.title}`} onClick={() => onEdit(plan)}>
                    <Pencil />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Удалить ${plan.title}`}
                    onClick={() => onDelete(plan.id)}
                    className="text-text-2 hover:bg-neg-soft hover:text-neg"
                  >
                    <Trash2 />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/** Скелет таблицы тарифов на время загрузки. */
export function PlansTableSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="flex flex-col" aria-busy="true" aria-label="Загрузка тарифов">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-6 border-t border-border-soft px-[18px] py-3.5">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-4 w-28" />
          <Skeleton className="ml-auto h-4 w-20" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-6 w-10 rounded-full" />
        </div>
      ))}
    </div>
  );
}
